import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
  XCircle,
} from "lucide-react";

const HouseGoLiveModal = ({
  open,
  loading,
  busy,
  error,
  readiness,
  onClose,
  onConfirm,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !busy && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, busy, loading, onClose]);

  const checkStats = useMemo(() => {
    const list = Array.isArray(readiness?.checks) ? readiness.checks : [];
    const total = list.length;
    const passed = list.filter((c) => c.ok).length;
    return { passed, total, list };
  }, [readiness]);

  if (!open) return null;

  const canActivate = Boolean(readiness?.canActivate);

  return createPortal(
    <div className="oc-go-live-root" role="dialog" aria-modal="true" aria-labelledby="oc-go-live-title">
      <button
        type="button"
        className="oc-go-live-backdrop"
        aria-label="Close"
        disabled={busy || loading}
        onClick={onClose}
      />
      <div className="oc-go-live-sheet">
        <button
          type="button"
          className="oc-go-live-close"
          aria-label="Close"
          disabled={busy || loading}
          onClick={onClose}
        >
          <X size={16} />
        </button>

        <p className="oc-go-live-eyebrow">Go live</p>
        <h2 id="oc-go-live-title" className="oc-go-live-title">
          Before you go live
        </h2>
        <p className="oc-go-live-sub">Every check must pass.</p>

        {loading ? (
          <div className="oc-go-live-loading">
            <Loader2 size={22} className="animate-spin" />
            <span>Checking house…</span>
          </div>
        ) : error ? (
          <p className="oc-go-live-error">{error}</p>
        ) : (
          <>
            <div className="oc-go-live-progress">
              <span>Checks</span>
              <strong>
                {checkStats.passed} / {checkStats.total} passed
              </strong>
            </div>
            <ul className="oc-go-live-checks">
              {checkStats.list.map((check) => {
                const isBlock = check.severity === "block";
                const ok = check.ok;
                const rowClass = ok
                  ? "oc-go-live-check-ok"
                  : isBlock
                    ? "oc-go-live-check-block"
                    : "oc-go-live-check-warn";
                const Icon = ok ? CheckCircle2 : isBlock ? XCircle : AlertTriangle;
                return (
                  <li key={check.id} className={`oc-go-live-check ${rowClass}`}>
                    <Icon size={16} aria-hidden />
                    <div>
                      <strong>{check.title}</strong>
                      {!ok && check.detail ? <p>{check.detail}</p> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className="oc-go-live-actions">
          <button
            type="button"
            className="oc-go-live-cancel"
            disabled={busy || loading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="oc-go-live-confirm"
            disabled={!canActivate || busy || loading}
            onClick={onConfirm}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Publishing…
              </>
            ) : (
              "Confirm & publish"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HouseGoLiveModal;
